import { Link, Outlet, ScrollRestoration, useLocation } from "react-router-dom"
import { useLogout } from "~/auth/use-logout"
import { useViewer } from "~/auth/use-viewer"
import { contactsPath, invitationsPath, rootPath, handlePath, adminUsersPath } from "~/common/paths"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/ui/dropdown-menu"
import { useToast } from "~/ui/use-toast"
import { UserAvatar } from "~/users/user-avatar"
import { cn } from "~/common/cn"
import { UserSearch } from "~/search/user-search"
import { UserRole } from "~/__generated__/graphql"
import Text from "~/ui/typography"
import { Users, Link2, Search } from "lucide-react"
import postalFunNavLogo from "~/images/postal-fun-nav-logo"

export const TopNavLayout = () => {
  const { viewer } = useViewer()
  const logout = useLogout()
  const { toast } = useToast()
  const location = useLocation()

  const isContactsActive = location.pathname === contactsPath({})
  const isInvitationsActive = location.pathname === invitationsPath({})

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top Navigation Header */}
      <header className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={rootPath({})} className="flex items-center">
            <img {...postalFunNavLogo} alt="Postal.Fun" />
          </Link>

          {/* Desktop Search Bar */}
          <div className="mx-8 hidden max-w-2xl flex-1 lg:block">
            <UserSearch placeholder="Search email, phone, or wallet address..." />
          </div>

          {/* Right Navigation Items */}
          <div className="flex items-center space-x-6">
            {/* Desktop Navigation */}
            <div className="hidden items-center space-x-6 md:flex">
              {/* Contacts */}
              <Link
                to={contactsPath({})}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
              >
                <Users
                  size={16}
                  className={cn("text-gray-600", isContactsActive && "text-postal-purple")}
                />
                <Text
                  variant="heading4_medium"
                  className={cn("text-nav-text", isContactsActive && "text-postal-purple")}
                >
                  Contacts
                </Text>
              </Link>

              {/* Invite Links */}
              <Link
                to={invitationsPath({})}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
              >
                <Link2
                  size={16}
                  className={cn("text-gray-600", isInvitationsActive && "text-postal-purple")}
                />
                <Text
                  variant="heading4_medium"
                  className={cn("text-nav-text", isInvitationsActive && "text-postal-purple")}
                >
                  Invite Links
                </Text>
              </Link>
            </div>

            {/* Mobile Navigation Icons */}
            <div className="flex items-center space-x-3 md:hidden">
              {/* Mobile Search */}
              <button
                onClick={() =>
                  toast({ title: "Search", description: "Search feature coming soon!" })
                }
                className="p-2 text-gray-600 hover:text-gray-900"
              >
                <Search size={20} />
              </button>

              {/* Mobile Contacts */}
              <Link to={contactsPath({})} className="p-2 text-gray-600 hover:text-gray-900">
                <Users size={20} className={cn(isContactsActive && "text-postal-purple")} />
              </Link>

              {/* Mobile Links */}
              <Link to={invitationsPath({})} className="p-2 text-gray-600 hover:text-gray-900">
                <Link2 size={20} className={cn(isInvitationsActive && "text-postal-purple")} />
              </Link>
            </div>

            {/* User Avatar/Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 focus:outline-none">
                {viewer && (
                  <UserAvatar
                    user={viewer}
                    className="h-8 w-8 rounded-full border border-[#999999] p-[3px]"
                  />
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="truncate">
                  {viewer?.email || "User"}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to={viewer?.handle ? handlePath({ handle: viewer.handle }) : rootPath({})}>
                    Profile
                  </Link>
                </DropdownMenuItem>
                {viewer?.userRole === UserRole.SystemAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to={adminUsersPath({})}>Admin Dashboard</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout}>Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 bg-gray-50">
        <Outlet />
        <ScrollRestoration />
      </main>
    </div>
  )
}
